import React, { useState, useMemo } from 'react';
import { Badge, Button, Dropdown, List, Typography, Space, Popover, Progress, Modal, Tag, Tabs } from 'antd';
import { DownloadOutlined, DeleteOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, ExclamationCircleOutlined, CloudDownloadOutlined, StopOutlined } from '@ant-design/icons';
import useGlobalStore from '@/store/useGlobalStore';
import useExportTaskManager from '@/hooks/useExportTaskManager';
import useDownloadTaskManager from '@/hooks/useDownloadTaskManager';
import { ExportTaskStatus, DownloadStatus } from '@/utils/exportTaskManager';
import { DownloadTaskStatus, DownloadTask } from '@/utils/downloadTaskManager';
import NotificationIcon from '@/components/icon/notification';
import clsx from 'clsx';
import styles from './main.module.less';
import './index.less';

const { Text } = Typography;
const ExportTaskIndicator: React.FC = () => {
    const { exportTasks, exportTasksCount, downloadTasks, downloadTasksCount } = useGlobalStore();
    const { removeTask: removeExportTask, clearCompletedTasks: clearExportTasks, retryDownload } = useExportTaskManager();
    const { removeTask: removeDownloadTask, clearCompletedTasks: clearDownloadTasks, cancelDownload } = useDownloadTaskManager();
    const [visible, setVisible] = useState(false);
    const [failModalVisible, setFailModalVisible] = useState(false);
    const [selectedFailInfos, setSelectedFailInfos] = useState<string[]>([]);
    const [activeTabKey, setActiveTabKey] = useState<string>('export');

    // 总任务数
    const totalTasksCount = useMemo(() => {
        return exportTasksCount + downloadTasksCount;
    }, [exportTasksCount, downloadTasksCount]);

    // 获取任务状态图标
    const getStatusIcon = (status: ExportTaskStatus, downloadStatus: DownloadStatus) => {
        if (status === ExportTaskStatus.PENDING) {
            return <LoadingOutlined style={{ color: '#1890ff' }} />;
        } else if (status === ExportTaskStatus.SUCCESS) {
            switch (downloadStatus) {
                case DownloadStatus.DOWNLOADING:
                    return <LoadingOutlined style={{ color: '#1890ff' }} />;
                case DownloadStatus.COMPLETED:
                    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
                case DownloadStatus.FAILED:
                    return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
                default:
                    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            }
        } else if (status === ExportTaskStatus.FAILED) {
            return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
        } else {
            return null;
        }
    };

    // 获取任务状态文本
    const getStatusText = (status: ExportTaskStatus, downloadStatus: DownloadStatus) => {
        if (status === ExportTaskStatus.PENDING) {
            return '导出中...';
        } else if (status === ExportTaskStatus.SUCCESS) {
            switch (downloadStatus) {
                case DownloadStatus.NOT_STARTED:
                    return '导出成功，准备下载';
                case DownloadStatus.DOWNLOADING:
                    return '下载中...';
                case DownloadStatus.COMPLETED:
                    return '下载完成';
                case DownloadStatus.FAILED:
                    return '下载失败';
                default:
                    return '导出成功';
            }
        } else if (status === ExportTaskStatus.FAILED) {
            return '导出失败';
        } else {
            return '未知状态';
        }
    };

    // 获取状态颜色
    const getStatusColor = (status: ExportTaskStatus) => {
        switch (status) {
            case ExportTaskStatus.PENDING:
                return '#1890ff';
            case ExportTaskStatus.SUCCESS:
                return '#52c41a';
            case ExportTaskStatus.FAILED:
                return '#ff4d4f';
            default:
                return '#d9d9d9';
        }
    };

    // 获取下载任务状态图标
    const getDownloadStatusIcon = (status: DownloadTaskStatus) => {
        switch (status) {
            case DownloadTaskStatus.PENDING:
                return <LoadingOutlined style={{ color: '#1890ff' }} />;
            case DownloadTaskStatus.DOWNLOADING:
                return <LoadingOutlined style={{ color: '#1890ff' }} />;
            case DownloadTaskStatus.SUCCESS:
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
            case DownloadTaskStatus.FAILED:
                return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
            case DownloadTaskStatus.CANCELLED:
                return <StopOutlined style={{ color: '#faad14' }} />;
            default:
                return null;
        }
    };

    // 获取下载任务状态文本
    const getDownloadStatusText = (task: DownloadTask) => {
        switch (task.status) {
            case DownloadTaskStatus.PENDING:
                return '等待下载...';
            case DownloadTaskStatus.DOWNLOADING:
                if (task.fileSize && task.downloadedSize) {
                    return `下载中 ${formatFileSize(task.downloadedSize)} / ${formatFileSize(task.fileSize)}`;
                }
                return `下载中... ${task.progress}%`;
            case DownloadTaskStatus.SUCCESS:
                return '下载完成';
            case DownloadTaskStatus.FAILED:
                return '下载失败';
            case DownloadTaskStatus.CANCELLED:
                return '已取消';
            default:
                return '未知状态';
        }
    };

    // 格式化文件大小
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // 处理重试下载
    const handleRetryDownload = (taskId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        retryDownload(taskId);
    };

    // 处理删除导出任务
    const handleRemoveExportTask = (taskId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        removeExportTask(taskId);
    };

    // 处理删除下载任务
    const handleRemoveDownloadTask = (taskId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        removeDownloadTask(taskId);
    };

    // 处理清除已完成的导出任务
    const handleClearExportTasks = () => {
        clearExportTasks();
    };

    // 处理清除已完成的下载任务
    const handleClearDownloadTasks = () => {
        clearDownloadTasks();
    };

    // 处理取消下载任务
    const handleCancelDownload = (taskId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        cancelDownload(taskId);
    };

    // 显示失败文件详情
    const showFailDetails = (failInfos: string[]) => {
        setSelectedFailInfos(failInfos);
        setFailModalVisible(true);
    };

    // 格式化时间
    const formatTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString();
    };

    // 导出任务列表内容
    const exportTaskListContent = (
        <div className="export-task-content">
            {exportTasks.length === 0 ? (
                <div className="empty-tasks">
                    <Text type="secondary">暂无导出任务</Text>
                </div>
            ) : (
                <>
                    <div className="task-list-header">
                        <Button
                            type="link"
                            size="small"
                            onClick={handleClearExportTasks}
                        >
                            清除已完成
                        </Button>
                    </div>
                    <List
                        size="small"
                        dataSource={exportTasks}
                        renderItem={(task) => (
                            <List.Item
                                key={task.id}
                                className={`task-item task-${task.status}`}
                                actions={[
                                    task.status === ExportTaskStatus.SUCCESS && task.downloadStatus === DownloadStatus.FAILED && (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<ReloadOutlined />}
                                            onClick={(e) => handleRetryDownload(task.id, e)}
                                            title="重新下载"
                                        />
                                    ),
                                    task.status !== ExportTaskStatus.PENDING && (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={(e) => handleRemoveExportTask(task.id, e)}
                                            title="删除任务"
                                        />
                                    )
                                ].filter(Boolean)}
                            >
                                <List.Item.Meta
                                    avatar={getStatusIcon(task.status, task.downloadStatus)}
                                    title={
                                        <div className="task-title">
                                            <Text ellipsis={{ tooltip: task.fileName }} style={{ maxWidth: 200 }}>
                                                {task.fileName}
                                            </Text>
                                        </div>
                                    }
                                    description={
                                        <div className="task-description">
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {getStatusText(task.status, task.downloadStatus)} · {formatTime(task.createTime)}
                                            </Text>
                                            {task.status === ExportTaskStatus.FAILED && task.failReason && (
                                                <div>
                                                    <Text type="danger" style={{ fontSize: '12px' }}>
                                                        {task.failReason}
                                                    </Text>
                                                </div>
                                            )}
                                            {task.exportFailInfos && task.exportFailInfos.length > 0 && (
                                                <div style={{ marginTop: 4 }}>
                                                    <Tag 
                                                        color="red" 
                                                        icon={<ExclamationCircleOutlined />}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            showFailDetails(task.exportFailInfos!);
                                                        }}
                                                    >
                                                        {task.exportFailInfos.length} 个文件导出失败
                                                    </Tag>
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                </>
            )}
        </div>
    );

    // 下载任务列表内容
    const downloadTaskListContent = (
        <div className="export-task-content">
            {downloadTasks.length === 0 ? (
                <div className="empty-tasks">
                    <Text type="secondary">暂无下载任务</Text>
                </div>
            ) : (
                <>
                    <div className="task-list-header">
                        <Button
                            type="link"
                            size="small"
                            onClick={handleClearDownloadTasks}
                        >
                            清除已完成
                        </Button>
                    </div>
                    <List
                        size="small"
                        dataSource={downloadTasks}
                        renderItem={(task) => (
                            <List.Item
                                key={task.id}
                                className={`task-item task-status-${task.status}`}
                                actions={[
                                    // 正在下载时显示取消按钮
                                    (task.status === DownloadTaskStatus.PENDING || task.status === DownloadTaskStatus.DOWNLOADING) && (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<StopOutlined />}
                                            onClick={(e) => handleCancelDownload(task.id, e)}
                                            title="取消下载"
                                            danger
                                        />
                                    ),
                                    // 已完成/失败时显示删除按钮
                                    (task.status !== DownloadTaskStatus.PENDING && task.status !== DownloadTaskStatus.DOWNLOADING) && (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<DeleteOutlined />}
                                            onClick={(e) => handleRemoveDownloadTask(task.id, e)}
                                            title="删除任务"
                                        />
                                    )
                                ].filter(Boolean)}
                            >
                                <List.Item.Meta
                                    avatar={getDownloadStatusIcon(task.status)}
                                    title={
                                        <div className="task-title">
                                            <Text ellipsis={{ tooltip: task.fileName }} style={{ maxWidth: 200 }}>
                                                {task.fileName}
                                            </Text>
                                        </div>
                                    }
                                    description={
                                        <div className="task-description">
                                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                                {getDownloadStatusText(task)} · {formatTime(task.createTime)}
                                            </Text>
                                            {task.status === DownloadTaskStatus.DOWNLOADING && (
                                                <Progress 
                                                    percent={task.progress} 
                                                    size="small" 
                                                    status="active"
                                                    style={{ marginTop: 4 }}
                                                />
                                            )}
                                            {task.status === DownloadTaskStatus.FAILED && task.failReason && (
                                                <div>
                                                    <Text type="danger" style={{ fontSize: '12px' }}>
                                                        {task.failReason}
                                                    </Text>
                                                </div>
                                            )}
                                        </div>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                </>
            )}
        </div>
    );

    // 任务列表内容（使用 Tabs）
    const taskListContent = (
        <div className="export-task-list">
            <Tabs
                activeKey={activeTabKey}
                onChange={setActiveTabKey}
                size="small"
                items={[
                    {
                        key: 'export',
                        label: (
                            <span>
                                导出任务
                                {exportTasksCount > 0 && (
                                    <Badge count={exportTasksCount} size="small" style={{ marginLeft: 4 }} />
                                )}
                            </span>
                        ),
                        children: exportTaskListContent
                    },
                    {
                        key: 'download',
                        label: (
                            <span>
                                下载任务
                                {downloadTasksCount > 0 && (
                                    <Badge count={downloadTasksCount} size="small" style={{ marginLeft: 4 }} />
                                )}
                            </span>
                        ),
                        children: downloadTaskListContent
                    }
                ]}
            />
        </div>
    );

    // 如果没有任务，不显示指示器
    // if (exportTasks.length === 0) {
    //     return null;
    // }

    return (
        <>
            <Popover
                content={taskListContent}
                title={null}
                trigger="click"
                open={visible}
                onOpenChange={setVisible}
                placement="bottomRight"
                overlayClassName="export-task-popover"
                overlayStyle={{ width: 350 }}
            >
                {/* <Badge count={exportTasksCount} size="small">
                    <Button
                        type="text"
                        icon={<DownloadOutlined />}
                        className="export-task-indicator"
                    />
                </Badge> */}
                <Badge className={styles.badge} count={totalTasksCount}>
                    <NotificationIcon className={clsx(styles.notificationIcon)} />
                </Badge>
            </Popover>

            <Modal
                title="导出失败文件列表"
                open={failModalVisible}
                onCancel={() => setFailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setFailModalVisible(false)}>
                        关闭
                    </Button>
                ]}
                width={600}
            >
                <List
                    size="small"
                    dataSource={selectedFailInfos}
                    renderItem={(failInfo) => (
                        <List.Item>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                                <Text type="danger" style={{ fontSize: '13px' }}>
                                    {failInfo}
                                </Text>
                            </div>
                        </List.Item>
                    )}
                />
            </Modal>
        </>
    );
};

export default ExportTaskIndicator;

